import { useEffect, useState } from "react";
import { UserPlus, Loader2, Trash2, ShieldCheck, Clock, Mail, Activity } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Team() {
  const { user } = useAuth();
  const isOwner = user?.role !== "staff";
  const [data, setData] = useState({ members: [], invites: [] });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", permission: "edit" });

  const load = () => api.get("/staff").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const [feed, setFeed] = useState([]);
  useEffect(() => { if (isOwner) api.get("/activity", { params: { limit: 60 } }).then(({ data }) => setFeed(data)).catch(() => {}); }, [isOwner]);

  const updatePerm = async (id, permission) => {
    try { await api.patch(`/staff/${id}`, { permission }); toast.success("Access updated"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const invite = async () => {
    if (!form.email) { toast.error("Enter an email"); return; }
    setSaving(true);
    try {
      await api.post("/staff/invite", form);
      toast.success(`Invitation sent to ${form.email}`);
      setOpen(false); setForm({ name: "", email: "", permission: "edit" }); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/staff/${id}`); toast.success("Team member removed"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8" data-testid="team-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-bold text-neutral-900">Your team</h2>
          <p className="text-sm text-neutral-500 mt-1">Invite teammates to your agency. Each teammate manages their own clients — you, as the owner, can see everyone's.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="invite-teammate-button">
              <UserPlus className="h-4 w-4 mr-2" /> Invite Teammate
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#F6EEEE] border-neutral-200">
            <DialogHeader><DialogTitle className="font-display">Invite a teammate</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label className="text-neutral-600">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-white border-neutral-200" placeholder="Jane Advocate" data-testid="team-invite-name" /></div>
              <div><Label className="text-neutral-600">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-white border-neutral-200" placeholder="jane@agency.com" data-testid="team-invite-email" /></div>
              <div>
                <Label className="text-neutral-600">Access level</Label>
                <Select value={form.permission} onValueChange={(v) => setForm({ ...form, permission: v })}>
                  <SelectTrigger className="mt-1.5 bg-white border-neutral-200" data-testid="team-invite-permission"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="edit">Full access — manage clients, disputes &amp; letters</SelectItem>
                    <SelectItem value="view">View only — cannot delete clients or send letters</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-neutral-500">They'll receive an email to set a password and get their own client workspace under your agency.</p>
            </div>
            <DialogFooter>
              <Button onClick={invite} disabled={saving} className="rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="team-invite-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Invitation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {data.members.map((m) => (
          <div key={m.id} className="rounded-xl border border-neutral-200 bg-white p-4 flex items-center justify-between gap-3" data-testid="team-member-row">
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-full bg-[#C1121F]/10 grid place-items-center text-[#C1121F] font-bold">{(m.name || m.email || "?").charAt(0).toUpperCase()}</span>
              <div>
                <div className="font-medium text-neutral-900">{m.name || m.email}</div>
                <div className="text-xs text-neutral-500">{m.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs px-2.5 py-1 rounded-md border bg-[#C1121F]/[0.06] text-[#9B0E18] border-[#C1121F]/20 inline-flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> {m.isOwner ? "Owner" : "Team member"}
              </span>
              {!m.isOwner && (isOwner ? (
                <Select value={m.permission || "edit"} onValueChange={(v) => updatePerm(m.id, v)}>
                  <SelectTrigger className="h-7 w-32 text-xs bg-white border-neutral-200" data-testid={`perm-select-${m.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="edit">Full access</SelectItem>
                    <SelectItem value="view">View only</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-xs px-2 py-1 rounded-md border bg-neutral-50 text-neutral-600 border-neutral-200">{m.permission === "view" ? "View only" : "Full access"}</span>
              ))}
              {isOwner && !m.isOwner && (
                <button onClick={() => remove(m.id)} className="text-neutral-400 hover:text-rose-600 transition-colors" data-testid="team-remove-button" title="Remove">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {data.invites.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3"><Mail className="h-4 w-4 text-[#C1121F]" /><h3 className="font-display font-bold text-neutral-900">Pending invitations</h3></div>
          <div className="space-y-3">
            {data.invites.map((iv, i) => (
              <div key={i} className="rounded-xl border border-neutral-200 bg-white p-4 flex items-center justify-between gap-3" data-testid="team-invite-row">
                <div><div className="font-medium text-neutral-900">{iv.name || iv.email}</div><div className="text-xs text-neutral-500">{iv.email}</div></div>
                <span className="text-xs px-2.5 py-1 rounded-md border bg-amber-50 text-amber-700 border-amber-200 inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {iv.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isOwner && (
        <div>
          <div className="flex items-center gap-2 mb-3"><Activity className="h-4 w-4 text-[#C1121F]" /><h3 className="font-display font-bold text-neutral-900">Team activity</h3></div>
          {feed.length === 0 ? <div className="text-sm text-neutral-400">No activity yet.</div> : (
            <div className="rounded-xl border border-neutral-200 bg-white divide-y divide-neutral-100" data-testid="team-activity-feed">
              {feed.map((a, i) => (
                <div key={i} className="p-3 flex items-start gap-3 text-sm">
                  <span className="h-8 w-8 shrink-0 rounded-full bg-[#C1121F]/10 grid place-items-center text-[#C1121F] text-xs font-bold">{(a.actor || "?").charAt(0)}</span>
                  <div className="flex-1">
                    <span className="font-medium text-neutral-900">{a.actor}</span>
                    <span className="text-neutral-600"> {(a.action || "").toLowerCase()}</span>
                    {a.consumerName && <span className="text-neutral-500"> · {a.consumerName}</span>}
                    {a.detail && <div className="text-xs text-neutral-400 mt-0.5">{a.detail}</div>}
                  </div>
                  <span className="text-[11px] text-neutral-400 whitespace-nowrap">{a.at ? new Date(a.at).toLocaleDateString() : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
